import type { FaqEntriesByLocale } from './faq';

/**
 * ### HOME_FAQ_BY_LOCALE
 *
 * Home FAQ copy, rendered on the home page and emitted as FAQPage structured
 * data from the same strings so the two can never drift. The entries answer
 * what a buyer asks before a first meeting — what the company is, what sound
 * AI solves, how it differs from inspecting by image, where it applies, and
 * how a deployment starts. Each answer stops at what and why; the per
 * inspection conditions behind a figure belong to the Listen AI Industrial FAQ,
 * and repeating them here would leave two copies of the same claim to keep in
 * step.
 *
 * Every figure names the inspection type and the measurement range it was
 * taken on, and press-release figures are never placed in the same sentence as
 * press-kit figures, because the two were measured on different scopes.
 *
 * @see {@link buildHomeStructuredData} Wraps these entries as schema.org Question nodes
 * @see {@link HomeFaqSection} Renders these entries on the home page
 * @see {@link INDUSTRIAL_FAQ_BY_LOCALE} Carries the per-inspection detail these answers defer to
 */
export const HOME_FAQ_BY_LOCALE: FaqEntriesByLocale = {
  ko: [
    {
      question: '디플리는 어떤 회사인가요?',
      answer:
        '디플리는 2017년에 설립된 인공지능(AI) 스타트업으로, 소리 분석 기술을 활용한 다양한 솔루션을 제공하는 회사입니다. 초기에는 아기 울음소리를 분석하는 서비스로 시작했으며, 현재는 제조, 보안, 방범 등 여러 분야에서 활동하고 있습니다.',
    },
    {
      question: '소리 AI는 제조 현장에서 어떤 문제를 해결하나요?',
      answer:
        '소리 AI는 사람의 귀에 의존하던 청각검사를 자동화합니다. 작업자가 귀나 손끝 감각으로 판단하던 검사는 피로도에 따라 판단이 달라지고 검사 이력도 데이터로 남지 않는데, Listen AI는 같은 판단을 소리 신호로 수행하고 OK/NG 결과를 기록으로 남깁니다. 대규모 청각검사 라인 운영에서는 60% 이상의 비용 절감이 보도된 바 있습니다. 검사 항목별 정확도와 측정 범위는 Listen AI Industrial 페이지의 FAQ에서 다룹니다.',
    },
    {
      question: '비전검사나 진동센서와 무엇이 다른가요?',
      answer:
        'Listen AI는 영상이나 진동이 아니라 소리 신호를 판단 근거로 삼습니다. 비전 센서가 놓치는 모터·액추에이터의 내부 결함까지, EOL 단계에서 24/7 전수 검사해 불량 유출을 99.8% 차단합니다. 이 수치는 EOL 단계 모터·액추에이터 전수 검사 기준이며, 다른 검사 항목의 수치와 합쳐 쓰지 않습니다. 커넥터 체결음처럼 사람 귀로는 구분하기 어려운 미세한 소리도 같은 방식으로 OK/NG를 판정합니다.',
    },
    {
      question: '어떤 공정과 설비에 적용할 수 있나요?',
      answer:
        'Listen AI Industrial의 기본 솔루션은 회전체 이음검사, 조립라인 커넥터 체결음 검사, 기계 노후화 검사 세 가지입니다. 양산 조립 공정에서는 OEM 조립 라인의 고소음을 극복하고, 커넥터 및 볼트의 연속 체결 작업을 99%+ 정확도로 실시간 감지합니다. 제품이 아니라 설비를 대상으로 하는 예지보전에서는 15TB 규모의 제조 현장 음향 데이터셋과 70가지 이상의 소리 유형 분석 경험을 기반으로, 설비 이상 신호를 사전에 감지해 다운타임을 사전에 차단합니다. 유리 파손·스크래치 감지, 반도체 부품 마모 진단, 로봇 설비 노후화 분석, 가스·압력 밸브 소리 분석 등의 적용 사례도 있습니다.',
    },
    {
      question: '도입 전에 어떤 데이터나 프로세스가 필요한가요?',
      answer:
        '필요한 것은 검사 대상 설비와 그 설비가 내는 소리입니다. 하드웨어를 설치하고 대상 설비의 체결음이나 작동음을 수집하면 디플리 Foundation Model을 기반으로 초기 평가를 시작할 수 있습니다. 커넥터 체결음 검사 보도자료 기준으로는 현장별 불량 데이터를 몇 주에서 몇 달에 걸쳐 모은 뒤에 시작하는 방식이 아니고 기존 라인의 설비·공구·레이아웃도 바꾸지 않지만, 이는 제조 음향 데이터에 특화된 Foundation Model을 전제로 한 설명이므로 모든 도입이 데이터 수집 없이 가능하다는 뜻은 아닙니다. 검사 이력은 MES(생산관리시스템) 또는 PLC와 연결해 추적 가능한 데이터로 남길 수 있습니다.',
    },
    {
      question: 'PoC는 어떤 순서로 진행하나요?',
      answer:
        'PoC는 하드웨어 설치, 대상 설비의 소리 수집, 초기 평가, 양산 라인 적용 순으로 진행합니다. 초기 평가는 디플리 Foundation Model 위에서 시작하며, PoC 결과는 양산 라인 적용으로 이어집니다. 마이크 구성과 분석 서버 배포 방식은 현장 소음 조건에 맞춰 정하고, 검사 항목별 조건은 Listen AI Industrial 페이지의 FAQ에서 다룹니다.',
    },
  ],
  en: [
    {
      question: 'What kind of company is Deeply?',
      answer:
        'Deeply is an AI startup founded in 2017 that delivers sound analysis solutions across manufacturing, safety, and security use cases.',
    },
    {
      question: 'What does sound AI solve on a manufacturing floor?',
      answer:
        'Sound AI automates the auditory inspection that used to rest on a person’s ears. A judgment made by ear or by fingertip shifts with the worker’s fatigue and leaves no inspection history behind as data, while Listen AI makes the same judgment from the sound signal and keeps every OK/NG result as a record. More than 60% cost reduction has been reported for large hearing inspection line operations. Accuracy and measurement range differ by inspection type, and the Listen AI Industrial page covers them per type in its own FAQ.',
    },
    {
      question:
        'How is this different from vision inspection or vibration sensors?',
      answer:
        'Listen AI judges from the sound signal rather than from an image or a vibration reading. It reaches the motor and actuator defects inside a product that a vision sensor misses, inspecting every unit 24/7 at the EOL stage and blocking 99.8% of defect escapes. That figure is scoped to full inspection of motors and actuators at the EOL stage and is not combined with figures from other inspection types. Sounds too faint for a person to tell apart, such as a connector engagement click, are judged the same way.',
    },
    {
      question: 'Which processes and equipment can it be applied to?',
      answer:
        'The three core Listen AI Industrial solutions are rotating-equipment noise inspection, assembly-line connector engagement sound inspection, and equipment aging detection. On the assembly process it overcomes the high noise of an OEM assembly line and detects continuous connector and bolt fastening work in real time with 99%+ accuracy. For predictive maintenance, which watches the equipment rather than the product, it draws on a 15TB manufacturing acoustic dataset and analysis experience across more than 70 sound types to catch a fault signal early and stop downtime before it starts. Deployed cases also cover glass breakage and scratch detection, semiconductor component wear diagnosis, robot equipment aging analysis, and gas and pressure valve sound analysis.',
    },
    {
      question: 'What data or process do we need before deploying?',
      answer:
        'What it takes is the equipment to be inspected and the sound that equipment makes. Once the hardware is installed and the engagement or operating sounds of the target equipment are collected, initial evaluation can begin on top of Deeply’s Foundation Model. Within the scope of the connector engagement sound press release, this does not wait on weeks or months of site-specific defect data and changes no existing line equipment, tools, or layout, but that account assumes a Foundation Model specialized in manufacturing acoustic data, so it does not mean every deployment needs no data collection at all. Inspection history can be linked to a MES (Manufacturing Execution System) or PLC and kept as traceable data.',
    },
    {
      question: 'How does a PoC proceed?',
      answer:
        'A PoC runs in four steps: install the hardware, collect sound from the target equipment, run the initial evaluation, then carry the result into production-line deployment. The initial evaluation starts on top of Deeply’s Foundation Model, and the PoC result is what carries into the production line. The microphone setup and the analysis server deployment are chosen against the noise conditions on site, and the Listen AI Industrial page covers the conditions per inspection type in its own FAQ.',
    },
  ],
} as const;
